import type { LessonTutorAvailabilityItem, LessonTutorAvailabilityParams } from '../../model/lessons/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface TutorsAvailabilityApiOptions {
  params: LessonTutorAvailabilityParams
  signal?: AbortSignal
}

/** `GET /admin-api/tutors/availability` — every matching tutor (not paginated) with its state at `start`.
 *  Null when the request failed or was cancelled by a newer one. */
export const useTutorsAvailabilityApi = async (
  options: TutorsAvailabilityApiOptions,
): Promise<LessonTutorAvailabilityItem[] | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const { start, discipline_id, booking_id, gender, special_features, accent, categories } = options.params

  const url = useAdminRoutes('admin.tutors.availability', {
    start,
    discipline_id,
    ...(booking_id ? { booking_id } : {}),
    gender,
    special_features,
    accent,
    categories,
  })

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url, { signal: options.signal })
    const tutors: LessonTutorAvailabilityItem[] = data.data?.tutors ?? []

    return tutors
  } catch (err: unknown) {
    if ((err as Error)?.name === 'CanceledError') return null

    toast.error(getAxiosErrorMessage(err))

    return null
  }
}
